import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { useAuth } from './AuthContext';
import { useApiKeys } from './ApiKeysContext';
import { useOnboardingState, type OnboardingStepName } from './useOnboardingState';
import { LoadingState } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { listOrgs } from '../../lib/api-client';

/**
 * Route guards for the onboarding flow (TTP-243 acceptance criteria:
 * "fluxo completo sem intervenção humana", "reload retoma a etapa correta",
 * "quem já completou não vê onboarding de novo").
 *
 * `useOnboardingState`'s `localStorage`-backed step is the day-to-day
 * source of truth for "which screen should I be on", but the `orgId`
 * custom claim (AuthContext) is the actual server truth for "does this user
 * have an org at all" — they can disagree on a fresh browser/device (org
 * already created elsewhere, local progress still at its default 'cnpj').
 * `effectiveStep` below reconciles that: if the claim already has an org
 * but local progress never got past 'cnpj', treat the effective step as
 * 'providers' (never re-show the CNPJ form for an org that already exists —
 * resubmitting the same CNPJ would just 409). The reconciled value is also
 * persisted back to `localStorage` so subsequent renders/reloads agree
 * without re-deriving it every time.
 *
 * TTP-266 incident fix: `localStorage` is NOT durable across
 * browsers/devices/a cleared profile — a real customer (Elo Editora) hit
 * this exactly, with the onboarding flow re-triggering on every fresh
 * session and `ensureOneApiKey` (ApiKeysContext.tsx via
 * `EnsureApiKeysReady` below) revoking-and-recreating their API keys each
 * time. `useServerOnboardingStatus` fetches `GET /internal/orgs` and, when
 * the active org's `onboarding_completed_at` is set, forces `effectiveStep`
 * straight to `'completed'` — a SERVER signal that overrides whatever
 * `localStorage` (or its absence) says, and is checked BEFORE `orgId &&
 * state.step === 'cnpj'` ever gets a chance to fall through into the
 * onboarding flow (and therefore before `EnsureApiKeysReady` could ever
 * mount and call `ensureKeys`).
 */

const STEP_ORDER: OnboardingStepName[] = ['cnpj', 'providers', 'demo', 'api-key', 'completed'];

function stepPath(step: OnboardingStepName): string {
  return step === 'completed' ? '/' : `/onboarding/${step}`;
}

interface ServerOnboardingStatus {
  /** The `orgId` this status was computed for — lets the render-time reset below detect an org change. */
  orgId: string | null;
  loading: boolean;
  completed: boolean;
}

/**
 * `true` while the `GET /internal/orgs` round trip for the active `orgId`
 * hasn't resolved yet (or there's no `orgId` to check). `completed` is
 * `true` only once that call resolved AND the active org's
 * `onboarding_completed_at` came back non-null — this is deliberately NOT
 * derived from `localStorage` at all, so it can be trusted independently of
 * whatever local progress does or doesn't exist.
 *
 * The `orgId !== status.orgId` block below resets `loading` to `true`
 * SYNCHRONOUSLY, during render (the same "adjust state while rendering"
 * pattern `useOnboardingState.tsx` uses for `uid` — see that file's
 * comment) rather than in a `useEffect`. This matters for the exact bug
 * this hook fixes: without it, the very FIRST render for a freshly-known
 * `orgId` would still see `loading === false` (the previous/initial value)
 * for one render, which is enough for `RequireCompletedOnboarding` to
 * compute `effectiveStep` from `localStorage` and navigate into
 * `/onboarding/providers` — mounting `EnsureApiKeysReady` — before the
 * `GET /internal/orgs` fetch below even starts.
 */
function useServerOnboardingStatus(user: ReturnType<typeof useAuth>['user'], orgId: string | null): { loading: boolean; completed: boolean } {
  const [status, setStatus] = useState<ServerOnboardingStatus>(() => ({
    orgId,
    loading: Boolean(user && orgId),
    completed: false,
  }));

  if (orgId !== status.orgId) {
    setStatus({ orgId, loading: Boolean(user && orgId), completed: false });
  }

  useEffect(() => {
    if (!user || !orgId) {
      return;
    }

    let cancelled = false;

    user
      .getIdToken()
      .then((idToken) => listOrgs(idToken))
      .then((orgs) => {
        if (cancelled) return;
        const membership = orgs.find((org) => org.id === orgId);
        setStatus({ orgId, loading: false, completed: Boolean(membership?.onboarding_completed_at) });
      })
      .catch(() => {
        // Network/API failure — fall back to the localStorage-based path
        // below rather than getting stuck; never treat a failed check as
        // "completed".
        if (!cancelled) {
          setStatus({ orgId, loading: false, completed: false });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user, orgId]);

  return { loading: status.loading, completed: status.completed };
}

function useEffectiveOnboarding() {
  const { user, orgId } = useAuth();
  const { state, update } = useOnboardingState();
  const serverStatus = useServerOnboardingStatus(user, orgId);

  const effectiveStep: OnboardingStepName = serverStatus.completed
    ? 'completed'
    : orgId && state.step === 'cnpj'
      ? 'providers'
      : state.step;

  useEffect(() => {
    if (effectiveStep !== state.step || (orgId && state.orgId !== orgId)) {
      update({ step: effectiveStep, orgId: orgId ?? state.orgId });
    }
    // Deliberately keyed only on the reconciled inputs, not `state`/`update`
    // (no `react-hooks` lint plugin is enabled in this repo's eslint.config.js
    // to enforce/complain about this either way) — `update` is a
    // `useCallback` stable per uid, and including the full `state` object
    // here would re-fire this effect on every persisted patch (including
    // the one this effect itself just wrote).
  }, [effectiveStep, orgId]);

  return {
    user,
    orgId,
    state: { ...state, step: effectiveStep },
    update,
    onboardingStatusLoading: serverStatus.loading,
  };
}

/** Wraps `/`, the post-onboarding app shell — redirects to /login (no session) or the right onboarding step (session but not done yet). */
export function RequireCompletedOnboarding({ children }: { children: ReactNode }) {
  const { user, orgId, state, onboardingStatusLoading } = useEffectiveOnboarding();
  const location = useLocation();

  if (user === undefined) {
    return <LoadingState label="Carregando sessão…" />;
  }
  if (user === null) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // TTP-266: wait for the SERVER onboarding-completion check before
  // deciding to redirect into the onboarding flow — this is what closes the
  // race that used to send an already-onboarded user (fresh browser, empty
  // localStorage) straight into `/onboarding/providers` (and from there
  // into `EnsureApiKeysReady`'s revoke-and-recreate path) before the
  // `GET /internal/orgs` check had a chance to say "already done".
  if (orgId && onboardingStatusLoading) {
    return <LoadingState label="Carregando sessão…" />;
  }
  if (state.step !== 'completed') {
    return <Navigate to={stepPath(state.step)} replace />;
  }
  return <>{children}</>;
}

/** Wraps /login and /signup — redirects an already-authenticated user to wherever they belong instead of showing the auth form again. */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { user, orgId, state, onboardingStatusLoading } = useEffectiveOnboarding();

  if (user === undefined) {
    return <LoadingState label="Carregando sessão…" />;
  }
  // TTP-266 (security-auditor finding): the exact same race
  // `RequireCompletedOnboarding` guards against also reaches through here —
  // right after `AuthStep` signs a user in, `onAuthStateChanged` can flip
  // `user`/`orgId` truthy in the SAME render `serverStatus.completed` is
  // still `false` (the `GET /internal/orgs` fetch hasn't resolved yet).
  // Without this check, `effectiveStep` would fall back to the
  // `localStorage`-derived value (`'providers'` for a fresh browser with a
  // real `orgId` claim) and `<Navigate>` straight into
  // `/onboarding/providers` SYNCHRONOUSLY — before the server had a chance
  // to say "already onboarded" — which then mounts `OnboardingStepGuard`
  // and, from there, `EnsureApiKeysReady`.
  if (user && orgId && onboardingStatusLoading) {
    return <LoadingState label="Carregando sessão…" />;
  }
  if (user) {
    return <Navigate to={stepPath(state.step)} replace />;
  }
  return <>{children}</>;
}

/**
 * Steps that call a `/v1` route (providers PUT, quotes POST) and therefore
 * need an onboarding API key in memory. `EnsureApiKeysReady` below is the
 * fallback path for "user reloaded mid-onboarding, ApiKeysContext lost its
 * in-memory keys" — the primary path (mint right after org creation) is
 * CnpjStep.tsx; this only kicks in when that didn't already happen in this
 * page load.
 */
const STEPS_REQUIRING_API_KEYS = new Set<OnboardingStepName>(['providers', 'demo', 'api-key']);

function EnsureApiKeysReady({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { keys, error, ensureKeys } = useApiKeys();
  const [attempted, setAttempted] = useState(false);
  const hasAnyKey = Boolean(keys.test || keys.live);

  useEffect(() => {
    if (!user || hasAnyKey || attempted) {
      return;
    }
    setAttempted(true);
    user.getIdToken().then((idToken) => {
      ensureKeys(idToken).catch(() => {
        // Surfaced via `error` below — nothing else to do with the rejection here.
      });
    });
  }, [user, hasAnyKey, attempted, ensureKeys]);

  if (hasAnyKey) {
    return <>{children}</>;
  }

  if (error) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Alert variant="error" title="Não foi possível preparar suas chaves de API">
          {error}
        </Alert>
        <Button type="button" variant="outline" onClick={() => setAttempted(false)}>
          Tentar novamente
        </Button>
      </div>
    );
  }

  return <LoadingState label="Preparando suas chaves de API…" />;
}

/** Wraps each `/onboarding/*` screen — enforces auth, forbids skipping ahead of the resolved step, allows revisiting the current or an earlier one, and never re-shows onboarding to a user who already completed it. */
export function OnboardingStepGuard({ step, children }: { step: OnboardingStepName; children: ReactNode }) {
  const { user, orgId, state, onboardingStatusLoading } = useEffectiveOnboarding();
  const location = useLocation();

  if (user === undefined) {
    return <LoadingState label="Carregando sessão…" />;
  }
  if (user === null) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // TTP-266 (security-auditor finding): this guard is the one that actually
  // mounts `EnsureApiKeysReady` below, so it's the last line of defense —
  // even if a caller somehow lands here directly (deep link, or before
  // `RequireCompletedOnboarding`/`RedirectIfAuthenticated` had a chance to
  // hold the redirect), never decide `effectiveStep` off a
  // still-in-flight server check.
  if (orgId && onboardingStatusLoading) {
    return <LoadingState label="Carregando sessão…" />;
  }

  // "quem já completou não vê onboarding de novo" (acceptance criterion) —
  // a completed user typing/following a link to an EARLIER onboarding step
  // (e.g. /onboarding/cnpj) must also bounce to '/', not just be blocked
  // from skipping AHEAD. This is a distinct case from the `requestedIndex >
  // allowedIndex` check below, which only guards the forward direction.
  if (state.step === 'completed') {
    return <Navigate to="/" replace />;
  }

  const requestedIndex = STEP_ORDER.indexOf(step);
  const allowedIndex = STEP_ORDER.indexOf(state.step);

  if (requestedIndex > allowedIndex) {
    return <Navigate to={stepPath(state.step)} replace />;
  }

  if (STEPS_REQUIRING_API_KEYS.has(step)) {
    return <EnsureApiKeysReady>{children}</EnsureApiKeysReady>;
  }

  return <>{children}</>;
}

export { useEffectiveOnboarding };
